// EmployeeTicketList.tsx

import * as React from "react";
import { useState, useCallback, useEffect, useMemo } from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
  MUIDataTableMeta,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmployeeEditTicket from "./EmployeeEditTicket";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import { Formik, FormikHelpers } from "formik";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { RootState } from "store";
import DateFormat from "examples/ReuseFunctions/DateFormat";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface TicketStatus {
  status_id: number;
  status_name: string;
}

interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}

interface Ticket {
  ticket_id: number;
  title: string;
  category_name: string;
  customer_id?: number;
  customer_name: string;
  customer_phone: string;
  priority_rank: string;
  description: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  created_at: string;
  assigned_on?: string | null;
  status_name: string;
  status_id: number;
  status_history: any;
  status_tracker: string[] | string | null;
  employee_name: string;
  employee_phone: string;
  pending_reason: string | null;
  onhold_reason: string | null;
  multimedia?: Media[] | null;
}

interface ArrivalFormValues {
  employee_arrival_date: string;
  arrival_time: string;
  reason_for_delay: string;
}

const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted components for table cells - defined outside main component
const PriorityCell: React.FC<{ value: string }> = React.memo(({ value }) => {
  const borderColor = "var(--primary-color, #2e7d32)";
  return (
    <span className={value ? "active-btn" : ""} style={{ borderColor }}>
      {value || "-"}
    </span>
  );
});

PriorityCell.displayName = "PriorityCell";

const DescriptionCell: React.FC<{ value: string }> = React.memo(({ value }) => {
  const displayValue =
    value?.length > 20 ? `${value.substring(0, 15)}...` : value;
  return (
    <Tooltip title={value}>
      <span>{displayValue}</span>
    </Tooltip>
  );
});

DescriptionCell.displayName = "DescriptionCell";

const AddressCell: React.FC<{
  value: string;
  city: string;
  state: string;
  addressType: string;
}> = React.memo(({ value, city, state, addressType }) => {
  const addressData = `${value}, ${city}, ${state},${addressType}`;
  const displayValue =
    addressData?.length > 20
      ? `${addressData.substring(0, 20)}...`
      : addressData;
  return (
    <Tooltip title={addressData}>
      <span>{displayValue}</span>
    </Tooltip>
  );
});

AddressCell.displayName = "AddressCell";

// Actions cell component
interface ActionsCellProps {
  ticketData: Ticket;
  onView: (ticketId: number) => void;
  onEdit: (ticket: Ticket) => void;
  onStatus: (ticket: Ticket) => void;
  onServiceUpdate: (ticket: Ticket) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = React.memo(
  ({ ticketData, onView, onEdit, onStatus, onServiceUpdate }) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <IconButton
          aria-label="view"
          onClick={() => onView(ticketData.ticket_id)}
        >
          <VisibilityIcon />
        </IconButton>

        {ticketData?.status_id === 3 && (
          <IconButton aria-label="edit" onClick={() => onEdit(ticketData)}>
            <EditIcon />
          </IconButton>
        )}

        {ticketData?.status_id === 2 && (
          <Button
            sx={{
              position: "relative",
            }}
            size="small"
            variant="contained"
            onClick={() => onStatus(ticketData)}
          >
            Start
          </Button>
        )}

        {ticketData?.status_id === 3 && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onServiceUpdate(ticketData)}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "auto",
              px: 1,
              fontSize: "0.75rem",
            }}
          >
            Service Update
          </Button>
        )}
      </Box>
    );
  }
);

ActionsCell.displayName = "ActionsCell";

// Renderer helper functions - OUTSIDE the component
const renderPriorityCell = (value: string) => <PriorityCell value={value} />;

const renderDescriptionCell = (value: string) => (
  <DescriptionCell value={value} />
);

const renderAddressCell = (value: string, tableMeta: MUIDataTableMeta) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data: Ticket = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return (
    <AddressCell
      value={value}
      city={data?.city_name}
      state={data?.state_name}
      addressType={data?.address_type}
    />
  );
};

const renderActionsCell = (
  tableMeta: MUIDataTableMeta,
  handleNavigateToView: (ticketId: number) => void,
  handleOpen: (ticket: Ticket) => void,
  handleStatus: (ticket: Ticket) => Promise<void>,
  handleServiceOpen: (ticket: Ticket) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const ticketData: Ticket =
    tableMeta?.tableData[currentTableData[rowIndex]?.index];

  return (
    <ActionsCell
      ticketData={ticketData}
      onView={handleNavigateToView}
      onEdit={handleOpen}
      onStatus={handleStatus}
      onServiceUpdate={handleServiceOpen}
    />
  );
};

const EmployeeTicketList: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [customerFilter, setCustomerFilter] = useState<{ customer_id: number; name: string } | null>(null);
  const [customers, setCustomers] = useState<{ customer_id: number; name: string }[]>([]);
  const [ticketStatuses, setTicketStatuses] = useState<TicketStatus[] | null>(
    null
  );
  const navigate = useNavigate();
  const [rows, setRows] = useState<Ticket[]>([]);
  const [serviceReason, setServiceReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState<string>("");
  const [closedStatusId, setClosedStatusId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isServiceOpen, setIsServiceOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const { fetchError, fetchSuccess } = useSnackbar();

  const formatDate = useCallback((dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }, []);

  const currentTime = DateFormat();

  const fetchData = useCallback(
    async (statusIdParam?: string | null) => {
      const statusIdToUse =
        statusFilter ||
        (statusIdParam ? Number.parseInt(statusIdParam, 10) : null);
      try {
        const endpoint =
          statusIdToUse === 1
            ? `/tickets`
            : `/tickets/employee/${user?.userId}`;

        const response = await axios.get(endpoint, {
          params: {
            status_id: statusIdToUse,
          },
        });

        let filteredData: Ticket[] = response.data?.list || [];
        if (!statusIdToUse && closedStatusId) {
          filteredData = filteredData.filter(
            (ticket) => ticket.status_id !== closedStatusId
          );
        }
        const mappedData = filteredData.map((ticket) => ({
          ...ticket,
          title: ticket.title || "-",
        }));

        const sortedData = [...mappedData].sort((a, b) => {
          const aMissing = !a.assigned_on ? 1 : 0;
          const bMissing = !b.assigned_on ? 1 : 0;
          if (aMissing !== bMissing) return aMissing - bMissing;
          const at = new Date(a.assigned_on as string).getTime();
          const bt = new Date(b.assigned_on as string).getTime();
          if (bt !== at) return bt - at;
          return (b.ticket_id ?? 0) - (a.ticket_id ?? 0);
        });

        setRows(sortedData);
      } catch (error) {
        setRows([]);
        throw error;
      }
    },
    [user?.userId, statusFilter, closedStatusId]
  );

  // Sync filter from URL (only when URL changes)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get("status_id");
    if (status) {
      const id = Number(status);
      if (!Number.isNaN(id)) setStatusFilter(id);
    }
  }, [location.search]);

  // Fetch rows when filters change
  useEffect(() => {
    fetchData(null).catch(() => {});
  }, [fetchData]);

  const fetchStatus = useCallback(async (): Promise<void> => {
    try {
      const statusResponse = await axios.get("/ticket-statuses");
      setTicketStatuses(statusResponse?.data);
      const closedStatus = statusResponse?.data.find(
        (s: TicketStatus) => s.status_name.toLowerCase() === "closed"
      );
      if (closedStatus) {
        setClosedStatusId(closedStatus.status_id);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }, []);

  const fetchCustomers = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get("/customer");
      const list = res.data?.list ?? [];
      setCustomers(
        list.map((c: { customer_id: number; name: string }) => ({
          customer_id: c.customer_id,
          name: c.name ?? "",
        }))
      );
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filteredRows = useMemo(() => {
    if (!customerFilter) return rows;
    return rows.filter(
      (t) => t.customer_id === customerFilter.customer_id
    );
  }, [rows, customerFilter]);

  const handleStatus = useCallback(
    async (ticket: Ticket): Promise<void> => {
      const statusdata = StatusHistory(
        ticket?.status_history,
        user?.name ?? "",
        ticket?.status_name
      );
      const ticketData: any = {
        status_id: 3,
        status_history: statusdata,
      };
      const trackerData = StatusTracker({
        previousData: Array.isArray(ticket?.status_tracker)
          ? ticket.status_tracker.join(",")
          : ticket?.status_tracker ?? null,
        message: "Work started",
        statusName: "In-Progress",
        status_id: 3,
        createdby: user?.name ?? "",
        employeeName: ticket?.employee_name ?? "",
        employeePhone: ticket?.employee_phone ?? "",
        arraivalDate: currentTime,
      });

      ticketData.status_tracker = trackerData;
      try {
        await axios.put(`/tickets/${ticket?.ticket_id}`, { ticketData });
        fetchData();
        fetchSuccess("Ticket updated to in-progress");
      } catch (err) {
        fetchError("Unable to Update Ticket");
        throw err;
      }
    },
    [user?.name, currentTime, fetchData, fetchSuccess, fetchError]
  );

  const handleServiceUpdate = useCallback(
    async (selectedTicket: Ticket): Promise<void> => {
      const reason =
        serviceReason === "Other" && customReason
          ? customReason
          : serviceReason || "Service Update from Employee";

      const onHoldReasons = [
        "Spare Parts Replacement",
        "Power Supply Issues",
        "Pending Material Availability",
      ];

      const isOnHold = onHoldReasons.includes(reason);
      const newStatusId = isOnHold ? 4 : 3;
      const newStatusLabel = isOnHold ? "On-Hold" : "In Progress";

      const trackerData = StatusTracker({
        previousData: Array.isArray(selectedTicket?.status_tracker)
          ? selectedTicket.status_tracker.join(",")
          : selectedTicket?.status_tracker ?? null,
        message: reason,
        statusName: newStatusLabel,
        status_id: newStatusId,
        createdby: user?.name ?? "",
        employeeName: selectedTicket?.employee_name ?? "",
        employeePhone: selectedTicket?.employee_phone ?? "",
        arraivalDate: currentTime,
      });

      const ticketData: any = {
        status_tracker: trackerData,
        status_id: newStatusId,
      };

      try {
        const response = await axios.put(
          `/tickets/${selectedTicket.ticket_id}`,
          {
            ticketData,
          }
        );

        fetchData();
        handleServiceClose();
        setServiceReason(null);
        setCustomReason("");
        fetchSuccess(response?.data?.message);
      } catch (err: any) {
        fetchError(err.response?.data?.error);
      }
    },
    [
      serviceReason,
      customReason,
      user?.name,
      currentTime,
      fetchData,
      fetchSuccess,
      fetchError,
    ]
  );

  const handleOpen = useCallback((ticketData: Ticket): void => {
    setSelectedTicket(ticketData);
    setIsEditOpen(true);
  }, []);

  const handleClose = useCallback((): void => {
    setIsEditOpen(false);
  }, []);

  const handleModalClose = useCallback((): void => {
    setIsModalOpen(false);
  }, []);

  const handleServiceOpen = useCallback((ticketData: Ticket): void => {
    setSelectedTicket(ticketData);
    setIsServiceOpen(true);
  }, []);

  const handleServiceClose = useCallback((): void => {
    setIsServiceOpen(false);
  }, []);

  const handleNavigateToView = useCallback(
    (ticketId: number): void => {
      navigate(`/employee/ticket-view/${ticketId}`, {
        state: { from: "/employee-tickets-list" },
      });
    },
    [navigate]
  );

  const serviceReasons: string[] = useMemo(
    () => [
      "Power Supply Issues",
      "Electrical Components Failure",
      "Overheating",
      "Loose or Damaged Wiring",
      "Software/Firmware Issues",
      "Physical Damage",
      "Remote Control or Interface Issues",
      "Voltage Fluctuations",
      "Spare Parts Replacement",
    ],
    []
  );

  // Memoize columns to prevent recreation on every render
  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      { name: "title", label: "Title" },
      { name: "category_name", label: "Category" },
      { name: "customer_name", label: "Customer" },
      { name: "customer_phone", label: "Phone" },
      {
        name: "priority_rank",
        label: "Priority",
        options: {
          filter: false,
          customBodyRender: renderPriorityCell,
        },
      },
      {
        name: "description",
        label: "Description",
        options: {
          filter: false,
          customBodyRender: renderDescriptionCell,
        },
      },
      {
        name: "address",
        label: "Address",
        options: {
          filter: false,
          customBodyRender: renderAddressCell,
        },
      },
      {
        name: "created_at",
        label: "Created At",
        options: {
          filter: false,
          customBodyRender: formatDate,
        },
      },
      { name: "status_name", label: "Status" },
      {
        name: "id",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) =>
            renderActionsCell(
              tableMeta,
              handleNavigateToView,
              handleOpen,
              handleStatus,
              handleServiceOpen
            ),
        },
      },
    ],
    [
      formatDate,
      handleNavigateToView,
      handleOpen,
      handleStatus,
      handleServiceOpen,
    ]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end", mb: 2 }}>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Status
            </SoftTypography>
            <Autocomplete
              options={ticketStatuses || []}
              getOptionLabel={(option) => option.status_name}
              value={ticketStatuses?.find((s) => s.status_id === statusFilter) ?? null}
              onChange={(event, value) => {
                setStatusFilter(value?.status_id ?? null);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Customer
            </SoftTypography>
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.name}
              value={customerFilter}
              onChange={(event, value) => setCustomerFilter(value)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All customers" />
              )}
            />
          </FormControl>
        </Box>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title="Manage Tickets"
              data={filteredRows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>

      {isEditOpen && selectedTicket && (
        <Modal open={isEditOpen} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }}
          >
            <EmployeeEditTicket
              employeeTicketData={selectedTicket}
              onClose={handleClose}
              fetchData={fetchData}
            />
          </Box>
        </Modal>
      )}

      {isServiceOpen && selectedTicket && (
        <Modal open={isServiceOpen} onClose={handleServiceClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }}
          >
            <Grid item xs={12}>
              <Box>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  Update Service
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    id="service-reason"
                    options={[...serviceReasons, "Other"]}
                    value={serviceReason}
                    onChange={(event, newValue) => {
                      if (newValue === "Other") {
                        setServiceReason("Other");
                      } else {
                        setServiceReason(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        style={{ fontSize: "12px" }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <ArrowDropDownIcon sx={{ marginLeft: "10px" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    freeSolo
                  />
                </FormControl>
                {serviceReason === "Other" && (
                  <Grid item xs={12} sx={{ marginTop: "20px" }}>
                    <Box>
                      <TextField
                        variant="outlined"
                        label="Other Reason"
                        onChange={(event) =>
                          setCustomReason(event.target.value)
                        }
                        value={customReason}
                        fullWidth
                        sx={{
                          "& .MuiInputLabel-root": { fontSize: "12px" },
                          "& .MuiInputBase-input": { fontSize: "12px" },
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "20px" }}>
              <Grid container spacing={2}>
                {serviceReason && (
                  <Grid item>
                    <SoftButton
                      className="success-btn"
                      variant="gradient"
                      color="success"
                      type="submit"
                      onClick={() => handleServiceUpdate(selectedTicket)}
                    >
                      Save
                    </SoftButton>
                  </Grid>
                )}
                <Grid item>
                  <SoftButton
                    className="cancel-btn"
                    variant="gradient"
                    color="warning"
                    onClick={handleServiceClose}
                  >
                    Cancel
                  </SoftButton>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      )}

      {isModalOpen && selectedTicket && (
        <Modal open={isModalOpen} onClose={handleModalClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }}
          >
            <Formik<ArrivalFormValues>
              initialValues={{
                employee_arrival_date: "",
                arrival_time: "",
                reason_for_delay: "",
              }}
              onSubmit={async (
                values: ArrivalFormValues,
                { setSubmitting }: FormikHelpers<ArrivalFormValues>
              ) => {
                const ticketData: any = {};
                const trackerData = StatusTracker({
                  previousData: Array.isArray(selectedTicket?.status_tracker)
                    ? JSON.stringify(selectedTicket.status_tracker)
                    : selectedTicket?.status_tracker ?? null,
                  message: values.reason_for_delay
                    ? `Engineer will arrive on ${values.employee_arrival_date} at ${values.arrival_time} due to ${values.reason_for_delay}`
                    : `Engineer will arrive on ${values.employee_arrival_date} at ${values.arrival_time}`,
                  statusName: "Todo",
                  status_id: 3,
                  createdby: user?.name ?? "",
                  employeeName: selectedTicket?.employee_name ?? "",
                  employeePhone: selectedTicket?.employee_phone ?? "",
                  arraivalDate: currentTime,
                });

                ticketData.status_tracker = trackerData;
                ticketData.employee_arrival_date = `${values.employee_arrival_date}T${values.arrival_time}`;
                try {
                  await axios.put(`/tickets/${selectedTicket?.ticket_id}`, {
                    ticketData,
                  });
                  fetchData();
                  handleModalClose();
                  fetchSuccess("Arrival date updated successfully");
                } catch (err) {
                  fetchError("Unable to Update Ticket");
                  throw err;
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {/* Add form fields here */}
            </Formik>
          </Box>
        </Modal>
      )}
    </DashboardLayout>
  );
};

export default EmployeeTicketList;
